import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getInternalGrossCodes,
  getApprovedGrossCodes,
  getInternalGrossCodeById,
  createInternalGrossCode,
  updateInternalGrossCode,
  approveInternalGrossCode,
  rejectInternalGrossCode,
  deleteInternalGrossCode,
  type CreateGrossCodeParams,
  type UpdateGrossCodeParams,
} from "../../services/internalGrossCode.service";

export function useInternalGrossCodes(filters?: {
  status?: string;
  search?: string;
  includeInactive?: boolean;
}) {
  return useQuery({
    queryKey: ["internal-gross-codes", filters],
    queryFn: () => getInternalGrossCodes(filters),
  });
}

export function useApprovedGrossCodes() {
  return useQuery({
    queryKey: ["internal-gross-codes", "approved"],
    queryFn: getApprovedGrossCodes,
    staleTime: 1000 * 60 * 5,
  });
}

export function useInternalGrossCode(id: string | undefined) {
  return useQuery({
    queryKey: ["internal-gross-code", id],
    queryFn: () => getInternalGrossCodeById(id!),
    enabled: !!id,
  });
}

export function useCreateInternalGrossCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: CreateGrossCodeParams) =>
      createInternalGrossCode(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["internal-gross-codes"] });
      queryClient.invalidateQueries({ queryKey: ["gross-codes"] });
    },
  });
}

export function useUpdateInternalGrossCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      params,
    }: {
      id: string;
      params: UpdateGrossCodeParams;
    }) => updateInternalGrossCode(id, params),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["internal-gross-codes"] });
      queryClient.invalidateQueries({
        queryKey: ["internal-gross-code", variables.id],
      });
      queryClient.invalidateQueries({ queryKey: ["gross-codes"] });
    },
  });
}

export function useApproveInternalGrossCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: string }) =>
      approveInternalGrossCode(id, userId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["internal-gross-codes"] });
      queryClient.invalidateQueries({
        queryKey: ["internal-gross-code", variables.id],
      });
      queryClient.invalidateQueries({ queryKey: ["gross-codes"] });
    },
  });
}

export function useRejectInternalGrossCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      userId,
      reason,
    }: {
      id: string;
      userId: string;
      reason: string;
    }) => rejectInternalGrossCode(id, userId, reason),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["internal-gross-codes"] });
      queryClient.invalidateQueries({
        queryKey: ["internal-gross-code", variables.id],
      });
    },
  });
}

export function useDeleteInternalGrossCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteInternalGrossCode(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["internal-gross-codes"] });
    },
  });
}
